// File: src/components/AirportList.jsx

/*
 * Critical Design
 * Do not modify this section without explicit permission
 * 
 * Airport list component that handles:
 * - Display of all airports in a searchable list
 * - Filtering by special use status
 * - Offline-capable data access
 * - Network status indication
 */

import React, { useState, useEffect } from 'react';
import { Search, X, ChevronRight, Menu as MenuIcon, Wifi, WifiOff } from 'lucide-react';
import { useLayout } from '../contexts/LayoutContext';
import { useNetwork } from '../contexts/NetworkContext';
import { useNavigate } from 'react-router-dom';
import { airportService } from '../services/airportService';
import Menu from './Menu';
import { useAuth } from '../contexts/AuthContext';

const AirportList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toggleMenu, isMenuOpen, closeMenu } = useLayout();
  const { isOnline, isSyncing, syncData } = useNetwork();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAirports = async () => {
      try {
        if (isOnline) {
          // When online, trigger a sync to refresh data
          await syncData();
        }
        // Get airports (will use cached data if offline)
        const data = await airportService.getAllAirports();
        setAirports(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching airports:', error);
        setAirports([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAirports();
  }, [isOnline]); // Re-run when online status changes

  const filteredAirports = airports.filter(airport => {
    if (!airport) return false;
    
    const searchLower = searchQuery.toLowerCase();
    
    // Extract hashtags from search query
    const searchTags = [];
    const cleanSearch = searchLower.replace(/#(\w+)/g, (match, tag) => {
      // Store the tag without the # symbol
      searchTags.push(tag.toLowerCase().replace(/#/g, ''));
      return ""; // Remove the tag from search string
    }).trim();
    
    // Check for tag matches (with partial matching)
    const airportTags = (airport.tags || []).map(t => t.toLowerCase().replace(/#/g, ''));
    
    // If there are explicit hashtags in the search
    if (searchTags.length > 0) {
      // Check if any search tag partially matches any airport tag
      const hasMatchingTag = searchTags.some(searchTag => 
        airportTags.some(airportTag => airportTag.includes(searchTag))
      );
      
      if (!hasMatchingTag) {
        return false; // Filter out this airport if no hashtags match
      }
    }
    
    // If there's remaining search text after extracting tags
    if (cleanSearch) {
      // Check standard fields
      const matchesStandardFields = (
        (airport.id?.toLowerCase() || '').includes(cleanSearch) ||
        (airport.airportName?.toLowerCase() || '').includes(cleanSearch) ||
        `${airport.city}, ${airport.state}`.toLowerCase().includes(cleanSearch)
      );
      
      // Also check if search text matches any tag (without requiring # prefix)
      const matchesTags = airportTags.some(tag => tag.includes(cleanSearch));
      
      return matchesStandardFields || matchesTags;
    }
    
    // If we only had hashtags in the search and they matched, or there was no search at all
    return true;
  });

  const handleAirportClick = (airport) => {
    navigate(`/airport/${airport.id}`);
  };

  const handleNewAirport = () => {
    navigate('/airport/new');
  };

  // Add helper function at the top of the component to check for special tags
  const hasSpecialTag = (airport) => {
    const tags = (airport.tags || []).map(tag => tag.toLowerCase().replace(/#/g, ''));
    return tags.includes('specialuse') || tags.includes('nightrestricted');
  };

  return (
    <div className="flex flex-col h-screen bg-page dark:bg-page-dark">
      {/* Menu Component */}
      <Menu />
      
      {/* Overlay when menu is open */}
      <div 
        className={`
          fixed inset-0 bg-page-dark bg-opacity-50 z-10
          transition-opacity duration-300
          ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={closeMenu}
      />

      {/* Network Status Bar */}
      <div className="flex-none">
        {(isSyncing || !isOnline) && (
          <div className={`
            flex items-center justify-center py-1 px-4 text-sm
            ${isOnline 
              ? 'bg-status-network-online-bg dark:bg-status-network-online-bg-dark text-status-network-online-text dark:text-status-network-online-text-dark'
              : 'bg-status-network-offline-bg dark:bg-status-network-offline-bg-dark text-status-network-offline-text dark:text-status-network-offline-text-dark'
            }
            transition-all duration-300
            ${!isOnline ? 'h-8' : 'h-4'}
          `}>
            {!isOnline ? (
              <>
                <WifiOff className="w-4 h-4 mr-2" />
                Offline Mode - Changes will sync when back online
              </>
            ) : isSyncing ? (
              <div className="flex items-center text-xs">
                <Wifi className="w-3 h-3 mr-1" />
                Syncing changes...
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Sticky Search Header */}
      <div className="flex-none bg-page dark:bg-page-dark shadow-surface dark:shadow-surface-dark">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleMenu}
              id="toggle-menu"
              name="toggle-menu"
              className={`
                p-2 rounded-lg
                hover:bg-surface-hover dark:hover:bg-surface-hover-dark
              `}
              aria-label="Toggle Menu"
            >
              <MenuIcon className="h-6 w-6 text-text-primary dark:text-text-primary-dark" />
            </button>
            <div className="relative flex-1">
              <label htmlFor="airport-search" className="sr-only">Search Airports</label>
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-text-tertiary dark:text-text-tertiary-dark" />
              </div>
              <input
                type="text"
                id="airport-search"
                name="airport-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`
                  block w-full pl-10 pr-10 py-2 rounded-lg
                  bg-page dark:bg-page-dark
                  border-default dark:border-default-dark
                  text-text-primary dark:text-text-primary-dark
                  focus:ring-button focus:border-button
                `}
                placeholder="Search or use #tag..."
                style={{ minHeight: '44px' }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  id="clear-search"
                  name="clear-search"
                  className="absolute inset-y-0 right-3 flex items-center"
                  aria-label="Clear search"
                >
                  <X className="h-5 w-5 text-text-tertiary dark:text-text-tertiary-dark" />
                </button>
              )}
            </div>
            <button
              onClick={handleNewAirport}
              id="add-airport"
              name="add-airport"
              className={`
                px-4 py-2 rounded-lg flex items-center gap-2
                text-text-primary-dark bg-button hover:bg-button-hover
                dark:bg-button-dark dark:hover:bg-button-hover-dark
                transition-colors duration-200
                ${!currentUser?.profile?.isEditor ? 'hidden' : ''}
              `}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
              </svg>
              Add Airport
            </button>
          </div>
        </div>
      </div>

      {/* Airport List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          {loading ? (
            <div className="flex justify-center items-center py-8 text-text-secondary dark:text-text-secondary-dark">
              Loading airports...
            </div>
          ) : filteredAirports.length === 0 ? (
            <div className="flex justify-center items-center py-8 text-text-secondary dark:text-text-secondary-dark">
              No airports found
            </div>
          ) : (
            filteredAirports.map((airport) => (
              <div
                key={airport.id}
                onClick={() => handleAirportClick(airport)}
                className={`
                  mt-4 p-4 rounded-lg shadow-sm border cursor-pointer
                  active:shadow-md active:scale-[0.99] transition-all
                  ${hasSpecialTag(airport) 
                    ? 'bg-special-airport dark:bg-special-airport-dark border-default dark:border-surface-dark' 
                    : 'bg-surface dark:bg-surface-dark border-default dark:border-surface-dark'
                  }
                `}
                style={{ minHeight: '88px' }}
              >
                <div className="flex justify-between items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-['Fira_Code'] text-xl font-bold flex-shrink-0 text-text-primary dark:text-text-primary-dark">
                        [{airport.id}]
                      </span>
                      <h2 className="text-xl font-bold truncate text-text-primary dark:text-text-primary-dark">
                        {airport.airportName}
                      </h2>
                    </div>
                    <p className="text-base mt-1 truncate text-text-secondary dark:text-text-secondary-dark">
                      {airport.city}, {airport.state}
                    </p>
                    
                    {/* Tags display */}
                    {airport.tags && airport.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2 max-w-full overflow-hidden">
                        {airport.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-button/20 text-button dark:bg-button-dark/30 dark:text-button-dark"
                          >
                            #{tag.replace(/#/g, '')}
                          </span>
                        ))}
                      </div>
                    )}
                    
                  </div>
                  <ChevronRight className="h-6 w-6 flex-shrink-0 text-text-tertiary dark:text-text-tertiary-dark" />
                </div>
              </div>
            ))
          )}
          {/* Add bottom spacer */}
          <div className="h-[100px]"></div>
        </div>
      </div>
    </div>
  );
};

export default AirportList; 